import { AxiosResponse } from 'axios'
import { http } from '../../utils'
import { Response } from '../types'

export interface SaleInvoiceEntryRequest {
  itemId?: number
  quantity: number
  rate: number
  isOtherCharge?: boolean
  itemName?: string
}

export interface CreateSaleInvoiceRequest {
  buyerCustomerId: number
  invoiceDate?: string
  invoiceNo?: string
  discount?: number
  /** NONE | CGST_SGST | IGST */
  gstType?: string
  /** Full combined GST rate (e.g. 18) */
  gstRate?: number
  reverseCharge?: boolean
  supplyNature?: string
  shipAdd1?: string
  shipAdd2?: string
  shipPlace?: string
  shipState?: string
  shipStateCode?: string
  shipPincode?: string
  entries: SaleInvoiceEntryRequest[]
}

export interface SaleAddressPreview {
  name?: string
  add1?: string
  add2?: string
  place?: string
  state?: string
  stateCode?: string
  pincode?: string
  fullAddress?: string
}

export interface SaleInvoicePreviewResponse {
  invoiceDate?: string
  invoiceNo?: string
  buyer: {
    id: number
    name: string
    gstin?: string
    state?: string
    stateCode?: string
    address?: string
    email?: string
    mobile?: string
  }
  billTo?: SaleAddressPreview
  shipTo?: SaleAddressPreview
  entries: Array<{
    itemId: number
    name: string
    isOtherCharge?: boolean
    quantity: number
    rate: number
    amount: number
  }>
  breakup: {
    total: number
    discount: number
    taxable: number
    igstRate: number
    cgstRate: number
    sgstRate: number
    igst: number
    cgst: number
    sgst: number
    invoiceTotal: number
  }
}

export interface SaleInvoice {
  id: number
  invoiceNo: string
  displayInvoiceNo?: string
  invoiceDate?: string
  createDate?: string
  partyName?: string
  partyId?: number
  customerId?: number
  taxableValue?: number
  cgstValue?: number
  sgstValue?: number
  igstValue?: number
  totalTax?: number
  invoiceTotal?: number
  discount?: number
  isGST?: boolean
  type?: string
  status?: string
  pdfLink?: string
  customerGSTIN?: string
  state?: string
  stateCode?: string
  eInvoiceNo?: string
  originalInvoiceId?: number
  accountId?: number
}

export interface SaleInvoicesListResponse extends Response {
  data: {
    bills: SaleInvoice[]
  }
}

export interface SaleInvoicePreviewApiResponse extends Response {
  data: SaleInvoicePreviewResponse
}

export interface CreateSaleInvoiceApiResponse extends Response {
  data: SaleInvoice
}

export type SaleBulkAction = 'SEND_EMAIL' | 'DELETE'

export function previewSaleInvoice(request: CreateSaleInvoiceRequest) {
  return http
    .post('/sale_invoice/pvt/preview', request)
    .then((response: AxiosResponse) => response.data as SaleInvoicePreviewApiResponse)
}

export function createSaleInvoice(request: CreateSaleInvoiceRequest) {
  return http
    .post('/sale_invoice/pvt', request)
    .then((response: AxiosResponse) => response.data as CreateSaleInvoiceApiResponse)
}

export function fetchSaleInvoices(
  from?: string,
  to?: string,
  isGst?: boolean | null,
  sortBy?: string
) {
  // from/to are already URL-encoded by getFormattedStart/EndDateString (same as AllBills).
  // Do not encodeURIComponent again or the server receives %2520 and date parse fails.
  let url = '/sale_invoice/pvt?'
  const params: string[] = []
  if (from) params.push(`from=${from}`)
  if (to) params.push(`to=${to}`)
  if (isGst === true || isGst === false) params.push(`is_gst=${isGst}`)
  if (sortBy) params.push(`sort_by=${sortBy}`)
  url += params.join('&')
  return http
    .get(url)
    .then((response: AxiosResponse) => response.data as SaleInvoicesListResponse)
}

export function deleteSaleInvoice(id: number) {
  return http
    .delete(`/sale_invoice/pvt/${id}`)
    .then((response: AxiosResponse) => response.data as Response)
}

export function fetchSaleInvoicePdf(id: number) {
  return http
    .get(`/sale_invoice/pvt/invoice_pdf?id=${id}`)
    .then((response: AxiosResponse) => response.data as Response & { data: string })
}

export function saleInvoiceBulkAction(request: {
  invoiceIds: number[]
  action: SaleBulkAction
}) {
  return http
    .post('/sale_invoice/pvt/bulk_action', request)
    .then((response: AxiosResponse) => response.data as Response)
}

export function fetchNextSaleInvoiceNo(invoiceDate?: string, isGst?: boolean) {
  let url = '/sale_invoice/pvt/next_inv_no?'
  const params: string[] = []
  if (invoiceDate) params.push(`invoice_date=${encodeURIComponent(invoiceDate)}`)
  if (isGst === true || isGst === false) params.push(`is_gst=${isGst}`)
  url += params.join('&')
  return http
    .get(url)
    .then((response: AxiosResponse) => response.data as Response & { data: string })
}

export function fetchSaleInvoicesPdf(
  from?: string,
  to?: string,
  isGst?: boolean | null,
  sortBy?: string
) {
  let url = '/sale_invoice/pvt/invoices_pdf?'
  const params: string[] = []
  if (from) params.push(`from=${from}`)
  if (to) params.push(`to=${to}`)
  if (isGst === true || isGst === false) params.push(`is_gst=${isGst}`)
  if (sortBy) params.push(`sort_by=${sortBy}`)
  url += params.join('&')
  return http
    .get(url)
    .then((response: AxiosResponse) => response.data as Response & { data: string })
}

export function fetchSaleInvoicesExcel(
  from?: string,
  to?: string,
  isGst?: boolean | null,
  sortBy?: string
) {
  let url = '/sale_invoice/pvt/invoices_excel?'
  const params: string[] = []
  if (from) params.push(`from=${from}`)
  if (to) params.push(`to=${to}`)
  if (isGst === true || isGst === false) params.push(`is_gst=${isGst}`)
  if (sortBy) params.push(`sort_by=${sortBy}`)
  url += params.join('&')
  return http
    .get(url)
    .then((response: AxiosResponse) => response.data as Response & { data: string })
}
